import { useEffect, useRef } from 'react';
import { useNotifications } from './use-notifications';

const NOTIFICATION_PREFIX_REGEX = /^\(\d+\)\s*/;

/**
 * Hook that updates the browser tab title with a notification count badge.
 */
export function useNotificationTitleBadge(isDisabled: boolean = false) {
    const { notifications } = useNotifications();
    const notificationCount = notifications.total;

    const baseTitleRef = useRef<string | null>(null);
    // Keep a ref to the current count so MutationObserver can access it.
    const countRef = useRef(notificationCount);
    countRef.current = notificationCount;

    useEffect(() => {
        if (isDisabled) return;
        if (typeof document === 'undefined') return;

        // Capture the base title (without any existing prefix) on first run.
        if (baseTitleRef.current === null) {
            baseTitleRef.current = document.title.replace(
                NOTIFICATION_PREFIX_REGEX,
                ''
            );
        }

        // Update title based on notification count.
        document.title = buildTitleWithCount(
            baseTitleRef.current,
            notificationCount
        );
    }, [notificationCount, isDisabled]);

    // Watch for title changes from Remix navigation and re-apply notification count.
    useEffect(() => {
        if (isDisabled) return;
        if (typeof document === 'undefined') return;

        const observer = new MutationObserver(() => {
            const currentTitle = document.title;
            const titleWithoutPrefix = currentTitle.replace(
                NOTIFICATION_PREFIX_REGEX,
                ''
            );
            if (titleWithoutPrefix !== baseTitleRef.current) {
                baseTitleRef.current = titleWithoutPrefix;
                const newTitle = buildTitleWithCount(
                    titleWithoutPrefix,
                    countRef.current
                );
                if (currentTitle !== newTitle) {
                    document.title = newTitle;
                }
            }
        });

        const titleElement = document.querySelector('title');
        if (titleElement) {
            observer.observe(titleElement, {
                childList: true,
                characterData: true,
                subtree: true,
            });
        }

        return () => {
            observer.disconnect();
        };
    }, [isDisabled]);
}

/**
 * Builds the title with notification count prefix.
 * @param baseTitle The base title without any notification prefix.
 * @param count The notification count.
 * @returns The title with notification count prefix if count > 0.
 */
function buildTitleWithCount(baseTitle: string, count: number): string {
    if (!baseTitle) {
        return baseTitle;
    }
    if (count > 0) {
        return `(${count}) ${baseTitle}`;
    }
    return baseTitle;
}
